import { Button } from "@/components/ui/UIElements/Button";
import React, { useState, useEffect } from "react";
import { NativeSelect } from "@/components/ui/UIElements/Select";
import { Modal, Switch, Input } from "../../";

const TYPE_OPTIONS = [
  { value: 1, label: "Included on price" },
  { value: 2, label: "Not included on price" },
];

const emptyRates = {
  delivery: { enabled: false, rate: "", type: 2 },
  pickup: { enabled: false, rate: "", type: 2 },
};

const toInitialState = (initialValues) => {
  if (!initialValues) {
    return {
      type: 1,
      name: "",
      rate: "",
      description: "",
      order_type_rates: { ...emptyRates },
    };
  }
  const otr = initialValues.order_type_rates || {};
  return {
    type: initialValues.type ?? 1,
    name: initialValues.name ?? "",
    rate: `${initialValues.rate ?? ""}`,
    description: initialValues.description ?? "",
    order_type_rates: {
      delivery: {
        enabled: !!otr.delivery,
        rate: otr.delivery ? `${otr.delivery.rate ?? ""}` : "",
        type: otr.delivery?.type ?? 2,
      },
      pickup: {
        enabled: !!otr.pickup,
        rate: otr.pickup ? `${otr.pickup.rate ?? ""}` : "",
        type: otr.pickup?.type ?? 2,
      },
    },
  };
};

const onlyDigits = (str) => str.replace(/\D/g, "").slice(0, 15);

const buildCreatePayload = (state) => {
  const payload = {
    type: Number(state.type),
    name: state.name.trim(),
    rate: Number(state.rate || 0),
    description: state.description.trim(),
  };
  const otr = {};
  ["delivery", "pickup"].forEach((k) => {
    const v = state.order_type_rates[k];
    if (v?.enabled && v.rate !== "") {
      otr[k] = { rate: Number(v.rate), type: Number(v.type) };
    }
  });
  if (Object.keys(otr).length) payload.order_type_rates = otr;
  return payload;
};

const diff = (a, b) => JSON.stringify(a) !== JSON.stringify(b);

const buildUpdatePayload = (initialValues, state) => {
  const initial = toInitialState(initialValues);
  const current = { ...state };
  const payload = {};
  if (diff(initial.type, current.type)) payload.type = Number(current.type);
  if (diff(initial.name, current.name)) payload.name = current.name.trim();
  if (diff(initial.rate, current.rate))
    payload.rate = Number(current.rate || 0);
  if (diff(initial.description, current.description))
    payload.description = current.description.trim();

  const buildOTR = (s) => {
    const out = {};
    ["delivery", "pickup"].forEach((k) => {
      const v = s.order_type_rates[k];
      if (v?.enabled && v.rate !== "") {
        out[k] = { rate: Number(v.rate), type: Number(v.type) };
      }
    });
    return out;
  };

  const initialOTR = buildOTR(initial);
  const currentOTR = buildOTR(current);
  if (diff(initialOTR, currentOTR)) payload.order_type_rates = currentOTR;
  return payload;
};

const TaxFormModal = ({
  isOpen,
  onClose,
  onSubmit,
  initialValues,
  isSubmitting,
}) => {
  const [state, setState] = useState(toInitialState(initialValues));

  useEffect(() => {
    setState(toInitialState(initialValues));
  }, [initialValues]);

  const setField = (field, value) =>
    setState((prev) => ({ ...prev, [field]: value }));
  const setRateField = (value) => setField("rate", onlyDigits(value));
  const setOTREnabled = (k, enabled) =>
    setState((prev) => ({
      ...prev,
      order_type_rates: {
        ...prev.order_type_rates,
        [k]: { ...prev.order_type_rates[k], enabled },
      },
    }));
  const setOTRRate = (k, value) =>
    setState((prev) => ({
      ...prev,
      order_type_rates: {
        ...prev.order_type_rates,
        [k]: { ...prev.order_type_rates[k], rate: onlyDigits(value) },
      },
    }));
  const setOTRType = (k, value) =>
    setState((prev) => ({
      ...prev,
      order_type_rates: {
        ...prev.order_type_rates,
        [k]: { ...prev.order_type_rates[k], type: Number(value) },
      },
    }));

  const handleSubmit = () => {
    const payload = initialValues
      ? buildUpdatePayload(initialValues, state)
      : buildCreatePayload(state);
    onSubmit(payload);
  };

  const canSubmit =
    state.name.trim() &&
    state.description.trim() &&
    state.rate !== "" &&
    !isSubmitting;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={initialValues ? "Edit Tax" : "Create Tax"}
      subtitle={initialValues ? "Update tax details" : "Add a new tax"}
      maxWidth="max-w-xl"
      className="ordering-catalog-surface"
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label
              htmlFor="tax-type"
              className="text-foreground mb-1 block text-sm font-medium"
            >
              Name *
            </label>
            <Input
              value={state.name}
              onChange={(e) => setField("name", e.target.value)}
              placeholder="Tax name"
            />
          </div>
          <div>
            <label className="text-foreground mb-1 block text-sm font-medium">
              Type
            </label>
            <NativeSelect
              id="tax-type"
              className="h-10 w-full"
              value={state.type}
              onChange={(e) => setField("type", Number(e.target.value))}
            >
              {TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </NativeSelect>
          </div>
          <div>
            <label className="text-foreground mb-1 block text-sm font-medium">
              Rate (%) *
            </label>
            <Input
              value={state.rate}
              onChange={(e) => setRateField(e.target.value)}
              placeholder="e.g. 8"
            />
          </div>
          <div>
            <label className="text-foreground mb-1 block text-sm font-medium">
              Description *
            </label>
            <Input
              value={state.description}
              onChange={(e) => setField("description", e.target.value)}
              placeholder="Short description"
            />
          </div>
        </div>

        <div className="pt-2">
          <div className="text-foreground mb-2 text-sm font-medium">
            Order type rates (optional)
          </div>
          {["delivery", "pickup"].map((k) => (
            <div
              key={k}
              className="border-border bg-card/60 mb-3 rounded-lg border"
            >
              <div className="flex items-center justify-between px-3 py-2">
                <span className="text-foreground capitalize">{k}</span>
                <Switch
                  checked={state.order_type_rates[k].enabled}
                  aria-label={k}
                  onCheckedChange={(v) => setOTREnabled(k, v)}
                />
              </div>
              {state.order_type_rates[k].enabled && (
                <div className="grid grid-cols-1 gap-3 px-3 pb-3 sm:grid-cols-2">
                  <div>
                    <label
                      htmlFor={`tax-${k}-type`}
                      className="text-muted-foreground mb-1 block text-xs"
                    >
                      Rate (%)
                    </label>
                    <Input
                      value={state.order_type_rates[k].rate}
                      onChange={(e) => setOTRRate(k, e.target.value)}
                      placeholder="e.g. 8"
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="text-muted-foreground mb-1 block text-xs">
                      Type
                    </label>
                    <NativeSelect
                      id={`tax-${k}-type`}
                      className="h-10 w-full"
                      value={state.order_type_rates[k].type}
                      onChange={(e) => setOTRType(k, e.target.value)}
                    >
                      {TYPE_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </NativeSelect>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <Button
            variant="ghost"
            onClick={onClose}
            className="border-border bg-secondary text-foreground rounded border px-4 py-2 text-sm"
          >
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="px-4 py-2 disabled:opacity-50"
          >
            {isSubmitting
              ? "Saving..."
              : initialValues
                ? "Save changes"
                : "Create tax"}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default TaxFormModal;
